// src/map/draw.client.ts — desen/editare de geometrii pe hartă (F13-T4).
// Manager Terra Draw VENDORAT și adaptat din vlib/gis/map_draw.client.ts (precedentul vendorării:
// lib/safe.ts). Diferența față de vlib: instanțiem TerraDraw DIRECT cu adaptorul MapLibre, fără
// controlul @watergis/maplibre-gl-terradraw — toolbar-ul e al aplicației, aici rămâne doar logica:
// moduri, snapshot, evenimente, selecție cu editare de vertecși. Client-only; scrierea pe server
// (POST/PATCH /api/features) e treaba apelantului (map.client), managerul nu știe de API.

import type maplibregl from "maplibre-gl"
import {
	TerraDraw,
	TerraDrawLineStringMode,
	TerraDrawPointMode,
	TerraDrawPolygonMode,
	TerraDrawRenderMode,
	TerraDrawSelectMode,
	type SnapToCustom,
} from "terra-draw"
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter"

// ─────────────────────────────────────────────────────────────
// SNAPPING (4.5(a), decizia lui Radu) — helperi PURI în coordonate ECRAN
// ─────────────────────────────────────────────────────────────
//
// Lipirea se decide în PIXELI (toleranța nu depinde de zoom), pe geometriile din jurul cursorului
// furnizate de apelant (`SnapSource` — map.client le ia din `queryRenderedFeatures` pe stratul
// editat și, opțional, pe straturile vizibile; contract: plan/contracts/snapping.md). Vertecșii au
// PRIORITATE față de muchii (un vertex la 10 px bate o muchie la 3 px — cartograful vrea colțul).
// Punctul de pe muchie se interpolează în lng/lat cu același parametru `t` ca pe ecran: Web
// Mercator e conform și local liniar la zoom-urile de editare, deci fără `unproject`.

export type ScreenXY = { x: number; y: number }
export type LngLat = [number, number]
export type ProjectFn = (lng: number, lat: number) => ScreenXY
export type UnprojectFn = (x: number, y: number) => { lng: number; lat: number }
/** Fereastră lng/lat `[min_lng, min_lat, max_lng, max_lat]` — pre-filtru ieftin înainte de `project`. */
export type SnapWindow = [number, number, number, number]

/** Toleranța implicită de lipire (pixeli). */
export const SNAP_TOLERANCE_PX = 12

export interface SnapQuery {
	lng: number
	lat: number
	x: number // pixeli în container
	y: number
	tolerance_px: number
}

/**
 * Sursa geometriilor de lipire — apelantul (map.client) întoarce geometriile din VECINĂTATEA
 * cursorului (o fereastră de ±tolerance_px), FĂRĂ geometria obiectului aflat în editare (altfel
 * vertexul tras s-ar lipi de propria poziție veche). Apelată la fiecare mousemove în timpul
 * desenului → trebuie să fie ieftină (queryRenderedFeatures, nu scanare de GeoJSON).
 */
export type SnapSource = (query: SnapQuery) => GeoJSON.Geometry[]

export interface SnapHit {
	position: LngLat
	kind: "vertex" | "edge"
	distance_px: number
}

/**
 * Secvențele de coordonate ale unei geometrii: Point → [[p]], LineString → [linia], Polygon →
 * inelele, Multi* → toate părțile, GeometryCollection → recursiv. Fără Z.
 */
export function geometry_rings(g: GeoJSON.Geometry): LngLat[][] {
	const xy = (c: GeoJSON.Position): LngLat => [c[0], c[1]]
	switch (g.type) {
		case "Point":
			return [[xy(g.coordinates)]]
		case "MultiPoint":
			return g.coordinates.map((c) => [xy(c)])
		case "LineString":
			return [g.coordinates.map(xy)]
		case "MultiLineString":
			return g.coordinates.map((l) => l.map(xy))
		case "Polygon":
			return g.coordinates.map((r) => r.map(xy))
		case "MultiPolygon":
			return g.coordinates.flatMap((p) => p.map((r) => r.map(xy)))
		case "GeometryCollection":
			return g.geometries.flatMap(geometry_rings)
		default:
			return []
	}
}

/** Cel mai apropiat punct de pe segmentul [a,b] de p (ecran) + parametrul t ∈ [0,1]. */
export function nearest_on_segment(
	p: ScreenXY,
	a: ScreenXY,
	b: ScreenXY,
): { point: ScreenXY; t: number } {
	const dx = b.x - a.x
	const dy = b.y - a.y
	const len2 = dx * dx + dy * dy
	if (len2 === 0) {
		return { point: { x: a.x, y: a.y }, t: 0 }
	}
	const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2))
	return { point: { x: a.x + t * dx, y: a.y + t * dy }, t }
}

function dist(a: ScreenXY, b: ScreenXY): number {
	return Math.hypot(a.x - b.x, a.y - b.y)
}

/**
 * Fereastra lng/lat a pătratului de ecran cursor ± toleranță (cele 4 colțuri dez-proiectate — și
 * pe hartă rotită bbox-ul colțurilor conține tot pătratul). Calculată O DATĂ per mousemove; apoi
 * `snap_to_geometries` sare vertecșii/muchiile ale căror bbox-uri n-o ating, fără să le proiecteze.
 * Fără falsuri negative: un punct la ≤ toleranță px de cursor stă în pătrat, deci în fereastră.
 */
export function snap_window(
	cursor: ScreenXY,
	tolerance_px: number,
	unproject: UnprojectFn,
): SnapWindow {
	const t = tolerance_px
	const corners = [
		unproject(cursor.x - t, cursor.y - t),
		unproject(cursor.x + t, cursor.y - t),
		unproject(cursor.x + t, cursor.y + t),
		unproject(cursor.x - t, cursor.y + t),
	]
	let min_lng = Infinity
	let min_lat = Infinity
	let max_lng = -Infinity
	let max_lat = -Infinity
	for (const c of corners) {
		min_lng = Math.min(min_lng, c.lng)
		min_lat = Math.min(min_lat, c.lat)
		max_lng = Math.max(max_lng, c.lng)
		max_lat = Math.max(max_lat, c.lat)
	}
	return [min_lng, min_lat, max_lng, max_lat]
}

function point_in_window(c: LngLat, w: SnapWindow): boolean {
	return c[0] >= w[0] && c[0] <= w[2] && c[1] >= w[1] && c[1] <= w[3]
}
/** Bbox-ul segmentului [a,b] atinge fereastra? (fals ⇒ niciun punct al segmentului nu e în ea) */
function segment_hits_window(a: LngLat, b: LngLat, w: SnapWindow): boolean {
	return !(
		Math.max(a[0], b[0]) < w[0] ||
		Math.min(a[0], b[0]) > w[2] ||
		Math.max(a[1], b[1]) < w[1] ||
		Math.min(a[1], b[1]) > w[3]
	)
}

/**
 * Lipirea cursorului (ecran) de cel mai apropiat VERTEX din toleranță; dacă nu există, de cel mai
 * apropiat punct de pe o MUCHIE din toleranță; altfel null. `opts` permite doar-vertecși /
 * doar-muchii și o `window` lng/lat (din `snap_window`) care sare vertecșii/muchiile din afara ei
 * ÎNAINTE de `project` — fără fereastră, se proiectează tot. Pură: geometriile vin deja filtrate
 * în jurul cursorului de la `SnapSource`.
 */
export function snap_to_geometries(
	cursor: ScreenXY,
	geometries: readonly GeoJSON.Geometry[],
	project: ProjectFn,
	tolerance_px: number = SNAP_TOLERANCE_PX,
	opts: { vertices?: boolean; edges?: boolean; window?: SnapWindow } = {},
): SnapHit | null {
	const want_vertices = opts.vertices !== false
	const want_edges = opts.edges !== false
	const win = opts.window
	let best_vertex: SnapHit | null = null
	let best_edge: SnapHit | null = null
	for (const g of geometries) {
		for (const ring of geometry_rings(g)) {
			// Proiecție LENEȘĂ, o dată per vertex — doar pentru ce trece de fereastră.
			const screen: Array<ScreenXY | undefined> = []
			const at = (i: number): ScreenXY => {
				const cached = screen[i]
				if (cached) {
					return cached
				}
				const p = project(ring[i][0], ring[i][1])
				screen[i] = p
				return p
			}
			if (want_vertices) {
				for (let i = 0; i < ring.length; i++) {
					if (win && !point_in_window(ring[i], win)) {
						continue
					}
					const d = dist(cursor, at(i))
					if (d <= tolerance_px && (!best_vertex || d < best_vertex.distance_px)) {
						best_vertex = { position: [ring[i][0], ring[i][1]], kind: "vertex", distance_px: d }
					}
				}
			}
			if (want_edges) {
				for (let i = 0; i + 1 < ring.length; i++) {
					const a = ring[i]
					const b = ring[i + 1]
					if (win && !segment_hits_window(a, b, win)) {
						continue
					}
					const { point, t } = nearest_on_segment(cursor, at(i), at(i + 1))
					const d = dist(cursor, point)
					if (d <= tolerance_px && (!best_edge || d < best_edge.distance_px)) {
						best_edge = {
							position: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t],
							kind: "edge",
							distance_px: d,
						}
					}
				}
			}
		}
	}
	return best_vertex ?? best_edge
}

/** Modurile de desen expuse în UI. `select` = editare de vertecși pe o formă existentă. */
export type DrawMode = "point" | "linestring" | "polygon" | "select"

export interface DrawnFeature {
	id: string
	type: "Feature"
	geometry: GeoJSON.Geometry
	properties: Record<string, unknown>
}

/**
 * Modurile de desen permise pentru un tip de geometrie de strat (PostGIS/GDAL:
 * „MultiPolygon", „LineString", „Point"…). Multi-variantele desenează forma simplă —
 * serverul acceptă orice geometrie GeoJSON validă pe strat.
 */
export function draw_modes_for_geometry(geometry_type: string): DrawMode[] {
	const gt = (geometry_type ?? "").toLowerCase()
	if (gt.includes("polygon")) {
		return ["polygon"]
	}
	if (gt.includes("line") || gt.includes("string") || gt.includes("curve")) {
		return ["linestring"]
	}
	if (gt.includes("point")) {
		return ["point"]
	}
	// Geometrie mixtă/necunoscută („Geometry", strat gol) — lăsăm toate formele.
	return ["point", "linestring", "polygon"]
}

/**
 * Împachetează o geometrie desenată (simplă) în familia stratului: pe un strat MultiPolygon,
 * poligonul desenat devine MultiPolygon cu o parte — exporturile (Ordin 904) și conformitatea
 * așteaptă familia stratului, nu forma simplă. Strat simplu/necunoscut = geometria neatinsă.
 */
export function wrap_geometry_for_layer(
	geometry: GeoJSON.Geometry,
	layer_geometry_type: string,
): GeoJSON.Geometry {
	const gt = (layer_geometry_type ?? "").toLowerCase()
	if (!gt.startsWith("multi")) {
		return geometry
	}
	if (gt.includes("polygon") && geometry.type === "Polygon") {
		return { type: "MultiPolygon", coordinates: [geometry.coordinates] }
	}
	if ((gt.includes("line") || gt.includes("string")) && geometry.type === "LineString") {
		return { type: "MultiLineString", coordinates: [geometry.coordinates] }
	}
	if (gt.includes("point") && geometry.type === "Point") {
		return { type: "MultiPoint", coordinates: [geometry.coordinates] }
	}
	return geometry
}

/** Taie coordonata Z recursiv ([lng,lat,z] → [lng,lat]); IDENTITATE păstrată dacă totul e 2D. */
function strip_z_coords(c: unknown): unknown {
	if (!Array.isArray(c)) {
		return c
	}
	if (typeof c[0] === "number") {
		return c.length > 2 ? [c[0], c[1]] : c
	}
	let changed = false
	const out = c.map((x) => {
		const r = strip_z_coords(x)
		if (r !== x) {
			changed = true
		}
		return r
	})
	return changed ? out : c
}

/** Varianta 2D a unei geometrii (fără GeometryCollection). Editorul lucrează strict în 2D. */
export function strip_z(geometry: GeoJSON.Geometry): GeoJSON.Geometry {
	if (geometry.type === "GeometryCollection") {
		return geometry
	}
	const coordinates = strip_z_coords(geometry.coordinates)
	if (coordinates === geometry.coordinates) {
		return geometry // deja 2D — nu clona degeaba (și testele de identitate rămân valabile)
	}
	return { ...geometry, coordinates } as GeoJSON.Geometry
}

/**
 * Pregătește o geometrie EXISTENTĂ pentru editorul de vertecși. TerraDraw nu știe Multi* —
 * un Multi* cu O SINGURĂ parte se despachetează în forma simplă (și `rewrap` o re-împachetează
 * la salvare); Multi* cu mai multe părți sau GeometryCollection = ne-editabil pe hartă (null).
 * ⚠️ Coordonata Z se TAIE (TerraDraw validează strict perechi [lng,lat] — datele reale F10-T2
 * vin [lng,lat,0]); editarea e 2D prin natură, geometria salvată pleacă 2D.
 */
export function unwrap_for_edit(
	geometry: GeoJSON.Geometry | null,
): { geometry: GeoJSON.Geometry; mode: "point" | "linestring" | "polygon"; rewrap: string } | null {
	if (!geometry) {
		return null
	}
	if (geometry.type !== "GeometryCollection") {
		geometry = strip_z(geometry)
	}
	switch (geometry.type) {
		case "Point":
			return { geometry, mode: "point", rewrap: "" }
		case "LineString":
			return { geometry, mode: "linestring", rewrap: "" }
		case "Polygon":
			return { geometry, mode: "polygon", rewrap: "" }
		case "MultiPoint":
			return geometry.coordinates.length === 1
				? {
						geometry: { type: "Point", coordinates: geometry.coordinates[0] },
						mode: "point",
						rewrap: "MultiPoint",
					}
				: null
		case "MultiLineString":
			return geometry.coordinates.length === 1
				? {
						geometry: { type: "LineString", coordinates: geometry.coordinates[0] },
						mode: "linestring",
						rewrap: "MultiLineString",
					}
				: null
		case "MultiPolygon":
			return geometry.coordinates.length === 1
				? {
						geometry: { type: "Polygon", coordinates: geometry.coordinates[0] },
						mode: "polygon",
						rewrap: "MultiPolygon",
					}
				: null
		default:
			return null
	}
}

/** Inversul lui `unwrap_for_edit`: re-împachetează geometria editată în familia originală. */
export function rewrap_geometry(geometry: GeoJSON.Geometry, rewrap: string): GeoJSON.Geometry {
	if (rewrap === "MultiPoint" && geometry.type === "Point") {
		return { type: "MultiPoint", coordinates: [geometry.coordinates] }
	}
	if (rewrap === "MultiLineString" && geometry.type === "LineString") {
		return { type: "MultiLineString", coordinates: [geometry.coordinates] }
	}
	if (rewrap === "MultiPolygon" && geometry.type === "Polygon") {
		return { type: "MultiPolygon", coordinates: [geometry.coordinates] }
	}
	return geometry
}

export interface DrawManagerEvents {
	/** O formă a fost TERMINATĂ (dublu-click/ultimul punct) în modurile de desen. */
	finish?: (feature: DrawnFeature) => void
	/** Geometria selectată s-a schimbat (drag de vertecși/mutare) — id-ul formei atinse. */
	update?: (feature: DrawnFeature) => void
	/** Selecția s-a schimbat: feature-ul selectat sau null la deselectare. */
	select?: (feature: DrawnFeature | null) => void
}

/** Stilul formelor în lucru — verde smarald, distinct de măsurare (roz) și selecție (cyan). */
const DRAW_STYLES = {
	fillColor: "#059669",
	fillOpacity: 0.25,
	outlineColor: "#047857",
	outlineWidth: 2,
	pointColor: "#059669",
	pointWidth: 6,
	pointOutlineColor: "#ffffff",
	pointOutlineWidth: 2,
} as const

export type DrawManager = ReturnType<typeof create_draw_manager>

/** Marcajul punctului de lipire (chihlimbar — distinct de schiță/selecție). */
const SNAP_POINT_STYLES = {
	snappingPointColor: "#f59e0b",
	snappingPointWidth: 7,
	snappingPointOutlineColor: "#ffffff",
	snappingPointOutlineWidth: 2,
} as const

export interface DrawManagerOptions {
	/** Sursa geometriilor de lipire (poate fi setată și ulterior cu `set_snap_source`). */
	snap_source?: SnapSource | null
	/** Toleranța în pixeli (implicit SNAP_TOLERANCE_PX). */
	snap_tolerance_px?: number
	/** Lipirea pornită la creare (implicit true — activă doar când există o sursă). */
	snapping?: boolean
}

/**
 * Creează managerul de desen peste o instanță MapLibre. Pornirea e LAZY (la primul set_mode) —
 * TerraDraw își adaugă sursele/straturile proprii pe hartă abia când chiar editezi.
 */
export function create_draw_manager(
	map: maplibregl.Map,
	events: DrawManagerEvents = {},
	options: DrawManagerOptions = {},
) {
	// Snapping (4.5a): stare de modul citită de `to_custom` la fiecare eveniment — comutarea NU cere
	// re-crearea modurilor (TerraDraw ține referința closure-ului).
	let snap_on = options.snapping ?? true
	let snap_source: SnapSource | null = options.snap_source ?? null
	let snap_tolerance = options.snap_tolerance_px ?? SNAP_TOLERANCE_PX

	function snap_here(
		x: number,
		y: number,
		lng: number,
		lat: number,
		project: ProjectFn,
		unproject: UnprojectFn,
	) {
		if (!snap_on || !snap_source) {
			return null
		}
		const geometries = snap_source({ lng, lat, x, y, tolerance_px: snap_tolerance })
		if (geometries.length === 0) {
			return null
		}
		// Fereastra lng/lat a toleranței, o dată per eveniment → vertecșii/muchiile din afara ei
		// nu se mai proiectează (queryRenderedFeatures întoarce geometrii ÎNTREGI, cu mii de vertecși).
		const window = snap_window({ x, y }, snap_tolerance, unproject)
		return snap_to_geometries({ x, y }, geometries, project, snap_tolerance, { window })
	}

	// Cârligul TerraDraw: la desen (polygon/linestring) și la tragerea vertecșilor (select) —
	// întoarce poziția lipită sau undefined (= poziția cursorului). Proiecția vine din adaptor.
	const to_custom: SnapToCustom = (event, context) => {
		const hit = snap_here(
			event.containerX,
			event.containerY,
			event.lng,
			event.lat,
			(lng, lat) => context.project(lng, lat),
			(x, y) => context.unproject(x, y),
		)
		return hit ? hit.position : undefined
	}

	const draw = new TerraDraw({
		// Terra Draw rotunjește FIECARE coordonată pe care o stochează: `Math.round(v * 10^p) / 10^p`,
		// cu p = 9 implicit. La latitudinea României 1e-9 grade ≈ 0,077 mm, deci un vârf lipit EXACT de
		// colțul vecinului (lipirea întoarce coordonata sursă verbatim) ajungea în bază la până la
		// ~0,04 mm de el — măsurat pe poligoane desenate real: 0,003–0,056 mm. Irelevant pentru cadastru,
		// dar destul cât raportul topologic să vadă o așchie microscopică în loc de o muchie comună.
		// ⚠️ 12, nu mai mult: rotunjirea înmulțește cu 10^p, iar la p = 15 produsul (27° × 1e15 ≈ 2,8e16)
		// depășește 2^53 și rotunjirea ar STRICA valoarea în loc s-o păstreze. La 12 rămâne ~0,08 µm.
		// ⚠️ E opțiune de ADAPTOR (BaseAdapter), nu a instanței TerraDraw — de acolo ajunge la moduri.
		adapter: new TerraDrawMapLibreGLAdapter({ map, coordinatePrecision: 12 }),
		modes: [
			new TerraDrawPointMode({
				styles: {
					pointColor: DRAW_STYLES.pointColor,
					pointWidth: DRAW_STYLES.pointWidth,
					pointOutlineColor: DRAW_STYLES.pointOutlineColor,
					pointOutlineWidth: DRAW_STYLES.pointOutlineWidth,
				},
			}),
			new TerraDrawLineStringMode({
				snapping: { toCustom: to_custom },
				styles: {
					lineStringColor: DRAW_STYLES.outlineColor,
					lineStringWidth: DRAW_STYLES.outlineWidth,
					...SNAP_POINT_STYLES,
				},
			}),
			new TerraDrawPolygonMode({
				snapping: { toCustom: to_custom },
				styles: {
					fillColor: DRAW_STYLES.fillColor,
					fillOpacity: DRAW_STYLES.fillOpacity,
					outlineColor: DRAW_STYLES.outlineColor,
					outlineWidth: DRAW_STYLES.outlineWidth,
					...SNAP_POINT_STYLES,
				},
			}),
			new TerraDrawSelectMode({
				// Editare completă de geometrie: drag pe formă, drag pe vertecși (cu lipire), vertecși
				// noi la mijloace de segment, ștergere de vertecși.
				flags: {
					point: { feature: { draggable: true } },
					linestring: {
						feature: {
							draggable: true,
							coordinates: {
								midpoints: true,
								draggable: true,
								deletable: true,
								snappable: { toCustom: to_custom },
							},
						},
					},
					polygon: {
						feature: {
							draggable: true,
							coordinates: {
								midpoints: true,
								draggable: true,
								deletable: true,
								snappable: { toCustom: to_custom },
							},
						},
					},
				},
			}),
			// Modul „pasiv": formele rămân vizibile, harta nu mai consumă click-uri de desen.
			new TerraDrawRenderMode({ modeName: "render", styles: {} }),
		],
	})

	let started = false
	let current_mode: DrawMode | null = null

	function snapshot_to_features(): DrawnFeature[] {
		if (!started) {
			return []
		}
		const out: DrawnFeature[] = []
		for (const f of draw.getSnapshot()) {
			if (!f) {
				continue
			}
			out.push({
				id: String(f.id),
				type: "Feature",
				geometry: f.geometry as GeoJSON.Geometry,
				properties: (f.properties as Record<string, unknown>) ?? {},
			})
		}
		return out
	}

	function find_feature(id: string): DrawnFeature | null {
		return snapshot_to_features().find((f) => f.id === id) ?? null
	}

	function ensure_started(): void {
		if (started) {
			return
		}
		draw.start()
		draw.setMode("render")
		started = true

		draw.on("finish", (id, ctx) => {
			// `finish` vine și de la drag-urile din select (action: dragFeature/dragCoordinate) —
			// pe acelea le raportăm ca update, nu ca formă nouă.
			let f = find_feature(String(id))
			if (!f) {
				return
			}
			// Modul punct n-are cârlig de snapping în TerraDraw → lipim punctul LA TERMINARE (aceeași
			// regulă vertex > muchie), înainte să-l raportăm apelantului.
			if (ctx && ctx.mode === "point" && f.geometry.type === "Point") {
				const [lng, lat] = f.geometry.coordinates
				const at = map.project([lng, lat])
				const hit = snap_here(
					at.x,
					at.y,
					lng,
					lat,
					(lo, la) => {
						const q = map.project([lo, la])
						return { x: q.x, y: q.y }
					},
					(x, y) => {
						const q = map.unproject([x, y])
						return { lng: q.lng, lat: q.lat }
					},
				)
				if (hit) {
					draw.updateFeatureGeometry(String(id), { type: "Point", coordinates: hit.position })
					f = find_feature(String(id)) ?? f
				}
			}
			if (ctx && ctx.mode === "select") {
				events.update?.(f)
			} else {
				events.finish?.(f)
			}
		})

		draw.on("select", (id) => {
			events.select?.(find_feature(String(id)))
		})
		draw.on("deselect", () => {
			events.select?.(null)
		})

		// Ștergerea unui vertex NU trece prin `finish` — doar prin `change:update`; fără el,
		// geometria editată n-ar fi marcată „modificată" și Salvează ar rămâne inert.
		draw.on("change", (ids, type) => {
			if (type !== "update" || current_mode !== "select") {
				return
			}
			const f = find_feature(String(ids[0] ?? ""))
			if (f) {
				events.update?.(f)
			}
		})
	}

	/** Comută modul de desen; null = pasiv (render). Pornirea e lazy, la primul apel. */
	function set_mode(mode: DrawMode | null): void {
		ensure_started()
		draw.setMode(mode ?? "render")
		current_mode = mode
	}

	function get_mode(): DrawMode | null {
		return current_mode
	}

	function get_features(): DrawnFeature[] {
		return snapshot_to_features()
	}

	/**
	 * Adaugă o geometrie existentă în editor (pentru editarea vertecșilor ei). Întoarce id-ul
	 * formei din editor sau "" dacă TerraDraw o respinge (geometrie exotică/invalidă).
	 * ⚠️ TerraDraw NU acceptă Multi* — apelantul le sparge în părți simple înainte.
	 */
	function add_feature(
		geometry: GeoJSON.Geometry,
		mode: "point" | "linestring" | "polygon",
	): string {
		ensure_started()
		const id = crypto.randomUUID()
		const res = draw.addFeatures([
			{ id, type: "Feature", geometry: geometry as never, properties: { mode } },
		])
		return res?.[0]?.valid ? id : ""
	}

	/** Selectează programatic o formă (intră pe editarea ei de vertecși). Cere modul `select`. */
	function select_feature(id: string): void {
		ensure_started()
		draw.selectFeature(id)
	}

	function remove_features(ids: string[]): void {
		if (!started || ids.length === 0) {
			return
		}
		draw.removeFeatures(ids)
	}

	/** Golește toate formele din editor (schițe + geometrii încărcate la editare). */
	function clear(): void {
		if (!started) {
			return
		}
		draw.clear()
	}

	/** Oprește complet editorul (își scoate sursele/straturile de pe hartă). */
	function destroy(): void {
		if (started) {
			draw.stop()
			started = false
			current_mode = null
		}
	}

	/** Pornește/oprește lipirea (butonul din toolbar — W5). Activă doar când există o sursă. */
	function set_snapping(on: boolean): void {
		snap_on = on
	}
	function is_snapping(): boolean {
		return snap_on
	}
	/** Sursa geometriilor de lipire (null = fără lipire, chiar dacă e pornită). */
	function set_snap_source(source: SnapSource | null): void {
		snap_source = source
	}
	function set_snap_tolerance(px: number): void {
		snap_tolerance = Number.isFinite(px) && px > 0 ? px : SNAP_TOLERANCE_PX
	}
	function get_snap_tolerance(): number {
		return snap_tolerance
	}

	return {
		set_mode,
		get_mode,
		get_features,
		add_feature,
		select_feature,
		remove_features,
		clear,
		destroy,
		set_snapping,
		is_snapping,
		set_snap_source,
		set_snap_tolerance,
		get_snap_tolerance,
	}
}
